import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarAlt,
    faTag,
    faChartLine,
    faDatabase,
    faUsers
} from '@fortawesome/free-solid-svg-icons';

interface BlogPostData {
    id: string;
    title: string;
    summary: string;
    content: string;
    date: string;
    tags: string[];
}

// Map of tag names to their corresponding icons
const tagIcons: { [key: string]: any } = {
    'Data': faDatabase,
    'Trends': faChartLine,
    'Industries': faChartLine,
    'Audience Size': faUsers,
    'default': faTag
};

// Every post here is written from the live SponsorDB database — real
// counts, pulled and checked before publishing, not AI-generated advice
// copy. Update the numbers periodically as the dataset grows rather than
// leaving them to go stale.
const blogPosts: { [key: string]: BlogPostData } = {
    '1': {
        id: '1',
        title: "What We've Tracked in Newsletter Sponsorships So Far",
        summary: "A look at every real sponsor-newsletter placement in the SponsorDB database — who's spending, on what kind of newsletter, and what the data does (and doesn't) tell us yet.",
        content: `We built SponsorDB to answer one question: which companies are actually paying to sponsor newsletters right now? Here's what the data actually shows — not projections, not a survey, just what we've tracked and verified so far.

As of today, we're tracking 91 real sponsor placements across 86 companies and 37 distinct newsletters, each with a contact we've verified directly.

Software and AI dominate. Software shows up in 37 of those placements, AI in 26, Business in 19, Technology in 15, and Marketing in 13. By a wide margin, the companies actively buying newsletter placements right now are B2B SaaS and AI tools. If you write about anything adjacent to those categories, there's real, current demand behind it.

One newsletter shows up more than any other. TLDR alone accounts for 19 of the placements we've tracked — roughly a fifth of everything in the database. The Neuron and Dense Discovery follow, with 9 and 6. That's not a coincidence: newsletters with a consistent publishing cadence and a tightly defined audience — tech, AI, productivity — are the ones sponsors keep coming back to.

One honest caveat. Our tracking today leans toward larger, more visible newsletters — the median tracked placement went to a newsletter with roughly 750,000 subscribers. That says less about who sponsors will work with, and more about which sponsorships are easiest to find and verify right now. We're actively working on surfacing more placements at the smaller end of the market, and we'll update this post as that data comes in.

If you want the current numbers for your own niche, our sponsor database updates as new placements get verified, and it's searchable by category for free.`,
        date: 'August 29, 2026',
        tags: ['Data', 'Trends']
    },
    '2': {
        id: '2',
        title: 'Which Industries Are Actually Paying to Sponsor Newsletters',
        summary: "A ranked breakdown of the categories with real, current sponsor demand — and what it means if your newsletter isn't in one of them yet.",
        content: `If you're deciding who to pitch first, the honest answer is: pitch where the money is already moving. Here's what we're actually seeing, ranked by how many verified sponsor placements we've tracked in each category.

Software leads with 37 placements, followed by AI at 26, Business at 19, Technology at 15, and Marketing at 13. Health, Productivity, Finance, Lifestyle, and Travel all show real activity too, just at smaller volume — five to seven placements each so far.

What this means if you write in Software, AI, Business, Technology, or Marketing: there's already a track record of companies in your space paying for newsletter placements. You can browse the current sponsors in your category directly in our database, with real company names, the newsletters they've sponsored, and — for subscribers — how to reach them.

What this means if you don't: it doesn't mean sponsors won't work with you. It means we've verified fewer placements in your niche so far. The categories above are the ones with the most sponsorship activity that's visible and trackable today, not an exhaustive map of every industry that sponsors newsletters. Categories like Health, Education, and Lifestyle already show real, if smaller, activity, and we're adding newly verified sponsors to the database every week.

Either way, the fastest way to know what's actually happening in your specific niche is to check it directly. Our database is searchable by category, free to preview, and only shows placements we've personally verified.`,
        date: 'August 30, 2026',
        tags: ['Data', 'Industries']
    },
    '3': {
        id: '3',
        title: 'How Big Does Your Newsletter Need to Be to Land a Sponsor?',
        summary: "We pulled the actual audience size of every newsletter placement in our database instead of guessing. Here's the real breakdown, and why it's not the whole story.",
        content: `This is the question we get asked the most, so we pulled the actual numbers instead of guessing.

Across the sponsor placements in our database with audience data attached: none went to newsletters under 10,000 subscribers, 14% went to newsletters between 10,000 and 50,000, 29% went to newsletters between 50,000 and 250,000, and 57% went to newsletters over 250,000. The median tracked placement went to a newsletter with about 750,000 subscribers.

At first glance, that looks discouraging if you're just starting out. It isn't the full picture, and here's why: our tracking process finds sponsorships by watching real newsletter sends, which means it's naturally biased toward newsletters that are already big enough to be widely subscribed to and easy to find. A 5,000-subscriber newsletter with a great sponsor deal is much harder for us — or anyone — to discover than one from a newsletter with half a million readers. Zero tracked placements under 10K almost certainly reflects a gap in what we can currently see, not a rule that sponsors won't pay for a smaller list.

What we can say with confidence: once a newsletter clears roughly 50,000 engaged subscribers, sponsorship activity gets a lot easier to find. Eighty-six percent of the placements we've verified are above that line. If you're under 50K, you're not locked out. You're competing with less public information about what a fair deal looks like, which is exactly the gap we built this database to close.

Search sponsors in your niche to see real placements, audience sizes, and contacts, or subscribe to our free weekly report for new sponsors as we verify them.`,
        date: 'August 31, 2026',
        tags: ['Data', 'Audience Size']
    }
};

const BlogPost = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const post = id ? blogPosts[id] : null;

    if (!post) {
        return (
            <div className="web-page blog-post">
                <div className="blog-post-container">
                    <div className="blog-post-error">Post not found</div>
                    <button onClick={() => navigate('/blog')} className="btn btn-accent">
                        Back to Blog
                    </button>
                </div>
            </div>
        );
    }

    const canonicalUrl = `https://sponsor-db.com/blog/${post.id}`;
    const articleJsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: post.title,
        description: post.summary,
        datePublished: post.date,
        author: { '@type': 'Organization', name: 'SponsorDB' },
        publisher: { '@type': 'Organization', name: 'SponsorDB' },
        mainEntityOfPage: canonicalUrl,
    };

    return (
        <div className="web-page blog-post">
            {/* Open Graph and Twitter Card tags deliberately omitted — see
                NicheLanding.tsx's comment; non-JS social crawlers never
                see a Helmet override. */}
            <Helmet>
                <title>{post.title} | SponsorDB</title>
                <meta name="description" content={post.summary} />
                <link rel="canonical" href={canonicalUrl} />
                <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
            </Helmet>
            <div className="blog-post-container">
                <article className="blog-post-content">
                    <h1 className="blog-post-title">{post.title}</h1>
                    <div className="blog-post-meta">
                        <div className="blog-post-meta-item">
                            <FontAwesomeIcon icon={faCalendarAlt} />
                            <span>{post.date}</span>
                        </div>
                    </div>
                    <div className="blog-post-tags">
                        {post.tags.map((tag, index) => (
                            <span key={index} className="blog-post-tag">
                                <FontAwesomeIcon icon={tagIcons[tag] || tagIcons.default} />
                                {tag}
                            </span>
                        ))}
                    </div>
                    <div className="blog-post-body">
                        {post.content.split('\n').map((paragraph, index) => (
                            <p key={index}>{paragraph}</p>
                        ))}
                    </div>
                    <div className="blog-post-cta">
                        <h3>Want the Numbers for Your Own Niche?</h3>
                        <p>
                            Search the live database by category and see real sponsor placements, audience sizes, and (for subscribers) verified contacts —
                            not estimates, the actual companies currently sponsoring newsletters like yours.
                        </p>
                        <button onClick={() => navigate('/signup')} className="btn btn-accent">
                            Get Started with SponsorDB
                        </button>
                    </div>
                    <button onClick={() => navigate('/blog')} className="btn btn-accent">
                        ← Back to Blog
                    </button>
                </article>
            </div>
        </div>
    );
};

export default BlogPost;
